import { tauxEnPourcents } from '@taki/data-model';
import type { Cell } from './contract.js';
import { nombreEnTexte } from '../lib/formats-intl.js';

/**
 * LES HABILLAGES DU SOCLE : ce qui s'AFFICHE a la place de la valeur brute,
 * quand le formatage n'est pas une decision d'app mais une regle de la
 * plateforme.
 *
 * POURQUOI ILS EXISTENT ICI. Un habillage est une FONCTION NOMMEE : la
 * declaration d'un ecran ecrit `"habillage": "euros"`, et le registre de l'app
 * fournit `euros`. C'etait tenable tant qu'une app etait ecrite a la main ;
 * une app NEE DU STUDIO n'a aucun TypeScript ou l'ecrire, donc elle ne pouvait
 * afficher ni une somme en euros, ni une date pure lisible. Chaque app ecrite
 * a la main, de son cote, recopiait les six memes lignes.
 *
 * CE QUI ENTRE ICI, ET CE QUI N'Y ENTRERA PAS. Y entre ce dont le sens est
 * porte par la PLATEFORME : une date PURE est une chaine `YYYY-MM-DD` par
 * doctrine (AGENTS.md section 7), donc la lire en francais n'est le choix de
 * personne. N'y entre pas ce qui parle du METIER (« Sony FX9 · ref. SNY-FX9 »,
 * deux initiales de categorie) : ca reste une fonction de l'app, et le registre
 * de l'app garde toujours la main sur un nom que ce catalogue porte aussi.
 */

/*
 * LES DEUX FORMATS D'UNE DEVISE (rond, a centimes), construits une fois par
 * devise et gardes : `Intl.NumberFormat` coute cher a construire, et un tableau
 * de mille montants l'appelle mille fois.
 */
const FORMATS_DE_DEVISE = new Map<string, { rond: Intl.NumberFormat; centimes: Intl.NumberFormat }>();

function formatsDe(devise: string): { rond: Intl.NumberFormat; centimes: Intl.NumberFormat } | null {
  const deja = FORMATS_DE_DEVISE.get(devise);
  if (deja !== undefined) return deja;
  try {
    const formats = {
      rond: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise, maximumFractionDigits: 0 }),
      centimes: new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: devise,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    };
    FORMATS_DE_DEVISE.set(devise, formats);
    return formats;
  } catch {
    /* Un code que le navigateur refuse (le schema n'accepte que trois
       majuscules, mais un document peut venir d'ailleurs) : pas de format. */
    return null;
  }
}

const JOUR = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/*
 * LE FORMAT A DECIMALES FIXES d'une devise (« 96,00 € »), garde comme les deux
 * autres, par devise et par nombre de decimales.
 */
const FORMATS_FIXES = new Map<string, Intl.NumberFormat | null>();

function formatFixe(devise: string, decimales: number): Intl.NumberFormat | null {
  const clef = `${devise}|${decimales}`;
  const deja = FORMATS_FIXES.get(clef);
  if (deja !== undefined) return deja;
  let format: Intl.NumberFormat | null;
  try {
    format = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: devise,
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  } catch {
    format = null;
  }
  FORMATS_FIXES.set(clef, format);
  return format;
}

/**
 * UN MONTANT DANS SA DEVISE (code ISO 4217, `EUR` par defaut). Sans `decimales`,
 * les centimes SEULEMENT QUAND IL Y EN A : on arrondit d'abord au centime, puis
 * un montant rond s'ecrit sans decimales (« 1 500 € », et 12.000000001 issu d'une
 * somme de flottants reste « 12 € »), tout autre en porte exactement deux
 * (« 8,75 € », « 0,50 € »). C'est la regle de `euros`, generalisee le
 * 27/10/2026 pour le type `montant` (`docs/socle/champs-types.md`) : une seule
 * regle d'ecriture d'une somme d'argent dans le produit, quelle que soit sa
 * devise.
 *
 * AVEC `decimales` (le reglage du champ, ou de la colonne native), le nombre de
 * decimales est FIXE, toujours : « 96,00 € » pour 2, « 96 € » pour 0
 * (`docs/socle/montant-decimales.md`). C'est ce qui aligne un prix rond et un
 * prix a centimes sur une meme fiche.
 *
 * Vide si la valeur n'est pas un nombre fini ; le nombre suivi du code quand la
 * devise est inconnue du navigateur.
 */
export function montant(valeur: Cell, devise: string = 'EUR', decimales?: number): string {
  if (typeof valeur !== 'number' || !Number.isFinite(valeur)) return '';
  if (decimales !== undefined) {
    const echelle = 10 ** decimales;
    const arrondi = Math.round(valeur * echelle) / echelle;
    const fixe = formatFixe(devise, decimales);
    if (fixe === null) return `${nombreEnTexte(arrondi, 'fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales })} ${devise}`;
    return fixe.format(arrondi);
  }
  const centimes = Math.round(valeur * 100);
  const formats = formatsDe(devise);
  if (formats === null) return `${nombreEnTexte(centimes / 100, 'fr-FR')} ${devise}`;
  return centimes % 100 === 0 ? formats.rond.format(centimes / 100) : formats.centimes.format(centimes / 100);
}

/**
 * Une somme, en euros : `montant` dans sa devise par defaut. Le nom reste,
 * parce que les declarations le citent (`"habillage": "euros"`) : aucun
 * montant rond ne change d'aspect, les montants a centimes ne sont plus
 * arrondis en silence, et toute app (celles nees du Studio, ou un « prix »
 * devient `euros`, comprises) en profite sans toucher a sa declaration.
 */
export function euros(valeur: Cell): string {
  return montant(valeur, 'EUR');
}

/**
 * Une DATE PURE (`YYYY-MM-DD`) en francais. La date se reconstruit en heure
 * LOCALE, jamais par `new Date(chaine)` que la norme interprete en UTC : a
 * l'ouest de Greenwich, le jour affiche serait la VEILLE du jour saisi.
 */
export function datePure(valeur: Cell): string {
  const texte = typeof valeur === 'string' ? valeur : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texte)) return '';
  return JOUR.format(
    new Date(Number(texte.slice(0, 4)), Number(texte.slice(5, 7)) - 1, Number(texte.slice(8, 10)), 12),
  );
}

/**
 * Un pourcentage, tel qu'il est stocke (0 a 100, a ne pas confondre avec `taux`
 * qui stocke une fraction), a la francaise : « 65 % »,
 * « 12,5 % » (et non « 12.5 % », le point d'un nombre JavaScript).
 */
export function pourcentage(valeur: Cell): string {
  return typeof valeur === 'number' && Number.isFinite(valeur)
    ? `${nombreEnTexte(valeur, 'fr-FR', { maximumFractionDigits: 2 })} %`
    : '';
}

/**
 * Un TAUX stocke en FRACTION (0,21, comme `taxRate` d'un produit), a la
 * francaise : « 21 % », « 5,5 % ». La valeur stockee reste la fraction, qui
 * entre telle quelle dans un calcul (`{socle:taxRate}`) ; seul l'affichage
 * passe en pourcents (`tauxEnPourcents`, sans le bruit binaire de 0,07 * 100).
 */
export function taux(valeur: Cell): string {
  return typeof valeur === 'number' && Number.isFinite(valeur)
    ? `${nombreEnTexte(tauxEnPourcents(valeur), 'fr-FR', { maximumFractionDigits: 2 })} %`
    : '';
}

const INSTANT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Un INSTANT (une date-heure, `Date`) en francais, AVEC son heure : par defaut
 * une cellule `Date` s'affiche par `toLocaleDateString()`, donc sans l'heure et
 * a la locale du navigateur. Une saisie d'heures, un rendez-vous, un pointage
 * perdent leur sens sans l'heure : c'est la plateforme qui porte ce sens
 * (AGENTS.md section 7, une date-heure est un instant absolu).
 */
export function dateHeure(valeur: Cell): string {
  return valeur instanceof Date && !Number.isNaN(valeur.getTime()) ? INSTANT.format(valeur) : '';
}

/**
 * Une DUREE stockee en MINUTES (le socle stocke `durationMinutes`, jamais des
 * heures decimales) : « 45 min », « 1 h », « 1 h 30 ». Un total en minutes
 * s'ecrit pareil, ce qui rend lisible le pied d'un tableau et le cumul d'une
 * carte liee. Une valeur qui n'est pas un nombre entier positif rend vide.
 */
export function duree(valeur: Cell): string {
  if (typeof valeur !== 'number' || !Number.isFinite(valeur) || valeur < 0) return '';
  const minutes = Math.round(valeur);
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  if (heures === 0) return `${reste} min`;
  if (reste === 0) return `${heures} h`;
  return `${heures} h ${String(reste).padStart(2, '0')}`;
}

/**
 * L'AGE D'UNE DATE, au jour d'aujourd'hui : « 3 ans » (24 mois et plus),
 * « 18 mois » / « 1 mois » (un mois et plus), « 12 jours » / « 1 jour » /
 * « 0 jour » sinon. Une date pure (`YYYY-MM-DD`) ou un instant (`Date`, ou sa
 * chaine ISO) : le jour se lit en heure LOCALE pour un instant, tel quel pour
 * une date pure. Vide quand la valeur ne se lit pas ou tombe dans le futur.
 *
 * C'EST UN AFFICHAGE : le tri, le filtre et l'export gardent la date brute
 * (« la date de naissance d'un animal » se trie par date, pas par « 3 ans »).
 * `maintenant` n'existe que pour les tests.
 */
export function age(valeur: Cell, maintenant: Date = new Date()): string {
  let naissance: [number, number, number];
  if (valeur instanceof Date) {
    if (Number.isNaN(valeur.getTime())) return '';
    naissance = [valeur.getFullYear(), valeur.getMonth() + 1, valeur.getDate()];
  } else if (typeof valeur === 'string' && /^\d{4}-\d{2}-\d{2}(?:$|T)/.test(valeur)) {
    naissance = [Number(valeur.slice(0, 4)), Number(valeur.slice(5, 7)), Number(valeur.slice(8, 10))];
  } else return '';
  const [annee, mois, jour] = naissance;
  const aujourdhui: [number, number, number] = [maintenant.getFullYear(), maintenant.getMonth() + 1, maintenant.getDate()];
  /* Un jour qui n'existe pas (le 31/02) ne se lit pas : Date.UTC le deborderait en silence. */
  const verifie = new Date(Date.UTC(annee, mois - 1, jour));
  if (verifie.getUTCFullYear() !== annee || verifie.getUTCMonth() !== mois - 1 || verifie.getUTCDate() !== jour) return '';
  const jours = Math.round((Date.UTC(...[aujourdhui[0], aujourdhui[1] - 1, aujourdhui[2]] as [number, number, number]) - verifie.getTime()) / 86_400_000);
  if (jours < 0) return '';
  const moisPleins = (aujourdhui[0] - annee) * 12 + (aujourdhui[1] - mois) - (aujourdhui[2] < jour ? 1 : 0);
  if (moisPleins >= 24) return `${Math.floor(moisPleins / 12)} ans`;
  if (moisPleins >= 1) return `${moisPleins} mois`;
  return `${jours} ${jours > 1 ? 'jours' : 'jour'}`;
}

/**
 * LE CATALOGUE, PAR NOM. C'est LUI que la declaration cite, et c'est LUI que le
 * formulaire du Studio propose : les deux lisent la meme table, donc le studio
 * ne peut pas offrir un habillage que le socle ne sait pas rendre.
 */
export const HABILLAGES_SOCLE: Readonly<Record<string, (valeur: Cell) => string>> = {
  euros,
  datePure,
  dateHeure,
  duree,
  pourcentage,
  taux,
  age,
};
